import { getThemeClass, getThemeComponentGroup } from "@/src_new/shared/lib/themeComponentClasses";
import clsx from "clsx";
import { useState } from "react";
import { Controller, type FieldPath, type FieldValues } from "react-hook-form";

import { formatAmountValue } from "@/src_new/shared/lib/amount";
import type { BaseInputProps } from "@/src_new/shared/types";

import { inputClassName } from "./TextInput";
import { WrapperInput } from "./WrapperInput";

type NumberInputProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = BaseInputProps<TFieldValues, TName> & {
  /** Allows one decimal point, pass false for counts like group size */
  allowDecimal?: boolean;
  /** Groups the digits while typing ("450000" -> "4,50,000"), on by default */
  grouped?: boolean;
  maxLength?: number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

/** Keeps digits only, plus a single decimal point when it is allowed */
const keepDigits = (value: string, allowDecimal: boolean) => {
  if (!allowDecimal) {
    return value.replace(/\D/g, "");
  }

  const [wholePart, ...decimalParts] = value.replace(/[^\d.]/g, "").split(".");

  return decimalParts.length ? `${wholePart}.${decimalParts.join("")}` : wholePart;
};

export function NumberInput<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  placeholder,
  description,
  disabled,
  required,
  className,
  allowDecimal = true,
  grouped = true,
  maxLength,
  onChange,
}: NumberInputProps<TFieldValues, TName>) {
  const inputClasses = getThemeComponentGroup("input");
  const [hasTypedText, setHasTypedText] = useState(false);
  const typedTextError = hasTypedText ? `${label} must be number` : undefined;

  return (
    <Controller
      control={control}
      name={name}
      rules={{
        required: required ? `${label} is required` : false,
      }}
      render={({ field, fieldState }) => {
        const rawValue = String(field.value ?? "");
        const displayValue = grouped ? formatAmountValue(rawValue) : rawValue;

        return (
          <WrapperInput
            label={label}
            error={fieldState.error?.message ?? typedTextError}
            description={description}
            required={required}
            className={className}
          >
            <input
              {...field}
              value={displayValue}
              type="text"
              inputMode={allowDecimal ? "decimal" : "numeric"}
              maxLength={maxLength}
              placeholder={placeholder}
              disabled={disabled}
              onChange={(event) => {
                const typedValue = event.target.value;
                const numericValue = keepDigits(typedValue, allowDecimal);

                // grouping adds the commas, anything else dropped here was text
                setHasTypedText(typedValue.replace(/,/g, "") !== numericValue);
                field.onChange(numericValue);
                onChange?.(event);
              }}
              onBlur={field.onBlur}
              className={clsx(
                inputClassName,
                getThemeClass(inputClasses.borderColor, "border-theme-border-strong"),
                getThemeClass(inputClasses.bgColor, "bg-theme-surface"),
                getThemeClass(inputClasses.textColor, "text-theme-text"),
                getThemeClass(
                  inputClasses.placeholderColor,
                  "placeholder:text-theme-text-placeholder"
                ),
                getThemeClass(inputClasses.focusBorderColor, "focus:border-theme-primary"),
                "focus:ring-2",
                getThemeClass(inputClasses.focusRingColor, "focus:ring-theme-primary/10"),
                inputClasses.base,
                (fieldState.error || typedTextError) &&
                  clsx(
                    inputClasses.errorBorderColor || "border-theme-danger-bright",
                    "focus:ring-theme-danger-bright/10"
                  )
              )}
            />
          </WrapperInput>
        );
      }}
    />
  );
}
